'use client';
import { Dispatch, ReactNode, SetStateAction, isValidElement, useCallback, useEffect, useRef, useState } from 'react';
import { useValidator } from '@/hooks';
import useErrorCount from '@/hooks/useErrorCount';
import { VALIDITY, US_STATES } from '@/constants';
import type { Config, Fields } from '@/hooks/useValidator/interfaces';
import { RenderRTE } from '@/components-2.0/structure/RenderRTE';
import { Grid } from '@/ui-components/atoms/Layout/Grid/Grid';
import { Text } from '@/ui-components/atoms/Text/Text';
import { StyledContainer } from '@/ui-components/atoms/Layout/StyledContainer/StyledContainer';
import { Button } from '@/ui-components/atoms/Button/Button';
import { Option, OptionGroup, Select } from '@/ui-components/atoms/Inputs/Select';
import { Input, InputType } from '@/ui-components/atoms/Inputs/Input/Input';
import { Checkbox } from '@/ui-components/atoms/Inputs/Checkbox/Checkbox';
import { RadioGroup } from '@/ui-components/atoms/Inputs/RadioGroup/RadioGroup';
import { RadioButton } from '@/ui-components/atoms/Inputs/Radio/Radio';
import { RadioIdVinInput } from '@/ui-components/atoms/Inputs/RadioIdVinInput/RadioIdVinInput';
import { Textarea } from '@/ui-components/atoms/Inputs/Input/Textarea';
import { Form, FormProps } from '@/ui-components/atoms/Inputs/Form/Form';
import { FormErrorDisplay } from '@/ui-components/atoms/Inputs/FormErrorDisplay/FormErrorDisplay';
import { FlexibleFormAlert } from './FlexibleFormAlert';
import styles from './flexibleForm.module.scss';
import cn from 'classnames';

import {
    createValidationConfigFields,
    FlexibleFormStyles,
    getFlexibleFieldInputProps,
    getValidationMessageType,
    normalizeFormData,
    SelectHeaderConfig,
    SubmitState,
    wrapFormElements,
} from './flexibleFormShared';

interface FlexibleFormBaseContext {
    validations: Fields;
    setValidations: Dispatch<SetStateAction<Fields>>;
    submitState: SubmitState;
    setSubmitState: Dispatch<SetStateAction<SubmitState>>;
}

interface FlexibleFormContentArgs extends FlexibleFormBaseContext {
    asset: SiteFlexibleForm;
    nestedForm?: boolean;
    wrappedFieldElements: ReactNode;
    duplicateError: ReactNode;
    duplicateErrorMessage: ReactNode;
    submitButton: ReactNode;
    errorDisplay: ReactNode | null;
}

interface FlexibleFormShellArgs {
    submitState: SubmitState;
    submitMessage: ReactNode;
    form: ReactNode;
}

export interface FlexibleFormVariant {
    formType: string;
    styles: FlexibleFormStyles;
    analytics?: {
        successEvent: string;
        failEvent: string;
    };
    sourceName?: string;
    trackSubmitAttempted?: boolean;
    passCheckboxRequired?: boolean;
    extraDropdownTypes?: string[];
    submitButtonFitting?: 'hug-contents' | 'fill-container';
    submitButtonDisabled?: boolean;
    emailValidator?: (value: string) => boolean;
    extraHiddenFields?: HiddenFormField[];
    getStateSelectHeader?: (fieldLabel: ReactNode) => SelectHeaderConfig;
    getDropdownSelectHeader?: (fieldLabel: ReactNode) => SelectHeaderConfig;
    getCheckboxClickHandler?: (fieldName: string) => React.MouseEventHandler<HTMLInputElement> | undefined;
    getCheckboxAdditionalProps?: (fieldName: string) => { [key: string]: unknown } | undefined;
    onValidSubmit?: (data: FormData, context: FlexibleFormBaseContext) => void;
    customSubmit?: (data: FormData, context: FlexibleFormBaseContext) => Promise<void>;
    onSuccessResponse: (response: Response, context: FlexibleFormBaseContext) => Promise<void>;
    renderContent: (args: FlexibleFormContentArgs) => ReactNode;
    renderSubmitMessage: (args: { asset: SiteFlexibleForm; submitState: SubmitState }) => ReactNode;
    renderShell: (args: FlexibleFormShellArgs) => ReactNode;
}

interface FlexibleFormBaseProps {
    asset: SiteFlexibleForm;
    nestedForm?: boolean;
    variant: FlexibleFormVariant;
    focusFirstFieldRef?: { current: (() => void) | null };
    onSearchByLicensePlate?: () => void;
    externalFormErrorMessage?: string | React.JSX.Element;
}

const defaultStateSelectHeader = () => ({
    title: 'Select a state',
    body: null,
});

const defaultDropdownSelectHeader = () => ({
    title: 'Select',
    body: null,
});

function getInputType(fieldType: string): InputType {
    if (fieldType === 'phone') {
        return 'tel';
    }

    if (fieldType === 'email only' || fieldType === 'email/confirmation') {
        return 'email';
    }

    return 'text';
}

export function FlexibleFormBase({
    asset,
    nestedForm = false,
    variant,
    focusFirstFieldRef,
    onSearchByLicensePlate,
    externalFormErrorMessage,
}: FlexibleFormBaseProps) {
    const { fields } = asset;
    const { form_fields, cta, hidden_form_fields } = fields;
    const fieldRefs = useRef<Record<string, { current: HTMLInputElement | HTMLButtonElement | null }>>({});
    const emailRef = useRef<HTMLInputElement>(null);
    const confirmEmailRef = useRef<HTMLInputElement>(null);
    const ctaFields = cta?.fields;
    const validationConfig: Config = {
        fields: createValidationConfigFields([...form_fields], emailRef, confirmEmailRef, variant.emailValidator),
    };

    const validator = useValidator(validationConfig);
    const [validations, setValidations] = useState(validator.getDefault());
    const [submitState, setSubmitState] = useState<SubmitState>('untouched');
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const errorCount = useErrorCount(validations);
    const context = { validations, setValidations, submitState, setSubmitState };
    const successEvent = variant.analytics?.successEvent;
    const failEvent = variant.analytics?.failEvent;
    const focusableFieldOrder = form_fields.flatMap((formField) => {
        const fieldType = formField.fields.type.toLowerCase();

        if (fieldType === 'header' || fieldType === 'text block') {
            return [];
        }

        if (fieldType === 'state and zip') {
            return [formField.fields.field_name, 'zip'];
        }

        return [formField.fields.field_name];
    });

    function getFieldRef(fieldName: string) {
        fieldRefs.current[fieldName] ||= { current: null };
        return fieldRefs.current[fieldName];
    }

    function getInputRef(fieldName: string) {
        return getFieldRef(fieldName) as { current: HTMLInputElement | null };
    }

    function getButtonRef(fieldName: string) {
        return getFieldRef(fieldName) as { current: HTMLButtonElement | null };
    }

    const focusFirstField = useCallback(() => {
        const firstFieldName = focusableFieldOrder.find((fieldName) => fieldRefs.current[fieldName]?.current);

        if (!firstFieldName) {
            return;
        }

        fieldRefs.current[firstFieldName]?.current?.focus();
    }, [focusableFieldOrder]);

    useEffect(() => {
        const browserWindow = window as BrowserWindow;
        if ((!successEvent && !failEvent) || !browserWindow._sxmDataLayerPush) {
            return;
        }

        if (submitState === 'success' && successEvent) {
            browserWindow._sxmDataLayerPush(successEvent);
        } else if (submitState === 'failed' && failEvent) {
            browserWindow._sxmDataLayerPush(failEvent);
        }
    }, [failEvent, submitState, successEvent]);

    useEffect(() => {
        if (!focusFirstFieldRef) {
            return;
        }

        focusFirstFieldRef.current = focusFirstField;

        return () => {
            focusFirstFieldRef.current = null;
        };
    }, [focusFirstField, focusFirstFieldRef]);

    useEffect(() => {
        if (!externalFormErrorMessage) {
            return;
        }

        setValidations((currentValidations) => ({
            ...currentValidations,
            form: {
                value: '',
                invalid: VALIDITY.invalid,
                validations: { ...currentValidations.form.validations, formerror: true },
                helperText: { ...currentValidations.form.helperText, formerror: externalFormErrorMessage },
            },
        }));
        setSubmitState('untouched');
    }, [externalFormErrorMessage]);

    function validateInput(input: HTMLInputElement) {
        const newValidations = validator.validateField(input);
        const updatedValidations = { ...validations, ...newValidations };
        setValidations(updatedValidations);

        return updatedValidations.form.invalid === VALIDITY.valid;
    }

    function validateForm(event: React.SyntheticEvent, fireAnalytics = false) {
        const newValidations = validator.validate(event);
        const updatedValidations = { ...validations, ...newValidations };
        setValidations(updatedValidations);

        const isValid = updatedValidations.form.invalid === VALIDITY.valid;

        if (!isValid && fireAnalytics) {
            const analyticsErrorList: object[] = [];
            Object.keys(updatedValidations).forEach((key: string) => {
                if (updatedValidations[key].invalid === 'invalid') {
                    Object.keys(updatedValidations[key].validations).forEach((validationKey: string) => {
                        if (!updatedValidations[key].validations[validationKey]) {
                            analyticsErrorList.push({
                                errorType: 'USER',
                                errorName: `${key} ${validationKey}`,
                                errorCode: '',
                            });
                        }
                    });
                }
            });
            if (analyticsErrorList.length > 0) {
                (window as BrowserWindow).pushToAdobe({
                    event: 'user-error',
                    errorInfo: analyticsErrorList,
                });
            }
        }

        return isValid;
    }

    const hiddenFields = [...(hidden_form_fields ?? []), ...(variant.extraHiddenFields ?? [])].map(
        (hiddenField, index) => <input key={index} type="hidden" name={hiddenField.name} value={hiddenField.value} />,
    );

    const dropdownTypes = ['dropdown', ...(variant.extraDropdownTypes || [])];

    const formFieldElements = form_fields.map((formField: SiteFormField) => {
        const {
            cms_id,
            fields: { field_name, aria_label, required, type, dropdown_radio_button_values, label, width },
        } = formField;
        const fieldType = type.toLowerCase();
        const fieldName = field_name;
        const fieldLabel = <RenderRTE contentfulDoc={label} />;
        const inputProps = getFlexibleFieldInputProps(formField);

        if (fieldType === 'state') {
            return (
                <Select
                    key={cms_id}
                    className={variant.styles.selectorStyles}
                    wrapperId={width === '50%' ? 'halfWidth' : 'fullWidth'}
                    selectWrapperStyles={width === '50%' ? variant.styles.halfWidth : variant.styles.fullWidth}
                    label={fieldLabel}
                    type="dropdown"
                    theme="grayLightWeb2"
                    name={fieldName}
                    buttonRef={getButtonRef(fieldName)}
                    invalid={validations[fieldName]?.invalid}
                    invalidMessage={validations[fieldName]?.helperText.required}
                    onBlur={(event) => validateInput(event.currentTarget)}
                    required={required}
                    dataCy={`${variant.formType}-form-select-state`}
                    errorDataCy={`${variant.formType}-form-select-state-err`}
                    selectHeader={(variant.getStateSelectHeader || defaultStateSelectHeader)(fieldLabel)}
                >
                    <OptionGroup>
                        {US_STATES.map((state) => (
                            <Option key={state.abbreviation} value={state.abbreviation}>
                                {state.name}
                            </Option>
                        ))}
                    </OptionGroup>
                </Select>
            );
        }

        if (fieldType === 'state and zip') {
            const zipFieldConfig = validationConfig.fields.zip;
            const zipMessageType = getValidationMessageType(zipFieldConfig, validations.zip);
            const zipInputProps = getFlexibleFieldInputProps({
                ...formField,
                fields: {
                    ...formField.fields,
                    type: 'Zip Code',
                    field_name: 'zip',
                },
            } as SiteFormField);

            return (
                <div key={cms_id} className={variant.styles.stateWrapper}>
                    <Grid container className={variant.styles.stateContainer}>
                        <Grid col={{ sm: 6 }}>
                            <Select
                                className={cn(variant.styles.selectorStyles, styles.selectContainer)}
                                label={fieldLabel}
                                type="dropdown"
                                theme="grayLightWeb2"
                                name={fieldName}
                                buttonRef={getButtonRef(fieldName)}
                                invalid={validations[fieldName]?.invalid}
                                invalidMessage={validations[fieldName]?.helperText.required}
                                onBlur={(event) => validateInput(event.currentTarget)}
                                required
                                dataCy={`${variant.formType}-form-select-state`}
                                errorDataCy={`${variant.formType}-form-select-state-err`}
                                selectHeader={(variant.getStateSelectHeader || defaultStateSelectHeader)(fieldLabel)}
                            >
                                <OptionGroup>
                                    {US_STATES.map((state) => (
                                        <Option key={state.abbreviation} value={state.abbreviation}>
                                            {state.name}
                                        </Option>
                                    ))}
                                </OptionGroup>
                            </Select>
                        </Grid>
                        <Grid col={{ sm: 6 }}>
                            <Input
                                key={cms_id}
                                ref={getInputRef('zip')}
                                className={variant.styles.zipInputStyles}
                                type="zip"
                                name="zip"
                                label="Zip Code"
                                invalid={validations.zip?.invalid}
                                required
                                onBlur={(event) => validateInput(event.currentTarget)}
                                invalidMessage={validations.zip?.helperText[zipMessageType]}
                                dataCy={`${variant.formType}-form-zip`}
                                errorDataCy={`${variant.formType}-form-zip-err`}
                                inputMode={zipInputProps.inputMode}
                                maxLength={zipInputProps.maxLength}
                                noSpacing
                            />
                        </Grid>
                    </Grid>
                </div>
            );
        }

        if (fieldType === 'header') {
            return <RenderRTE contentfulDoc={label} key={cms_id} className={variant.styles.formSectionHeaderStyles} />;
        }

        if (fieldType === 'text block') {
            return (
                <RenderRTE contentfulDoc={label} key={cms_id} className={variant.styles.formSectionTextBlockStyles} />
            );
        }

        if (dropdownTypes.includes(fieldType)) {
            return (
                <Select
                    key={cms_id}
                    className={variant.styles.dropdownSelectStyles}
                    label={fieldLabel}
                    type="dropdown"
                    theme="grayLightWeb2"
                    name={fieldName}
                    buttonRef={getButtonRef(fieldName)}
                    invalid={validations[fieldName]?.invalid}
                    invalidMessage={validations[fieldName]?.helperText?.required}
                    onBlur={(event) => validateInput(event.currentTarget)}
                    required={required}
                    dataCy={`${variant.formType}-form-select-${fieldName}`}
                    errorDataCy={`${variant.formType}-form-select-${fieldName}-err`}
                    selectHeader={(variant.getDropdownSelectHeader || defaultDropdownSelectHeader)(fieldLabel)}
                >
                    <OptionGroup>
                        {dropdown_radio_button_values?.map((option) => (
                            <Option key={option.id} value={option.key}>
                                {option.value}
                            </Option>
                        ))}
                    </OptionGroup>
                </Select>
            );
        }

        if (fieldType === 'checkbox' || fieldType === 'checkbox legal') {
            return (
                <Checkbox
                    key={cms_id}
                    name={fieldName}
                    value="Y"
                    invalid={validations[fieldName]?.invalid}
                    onBlur={(event) => validateInput(event.currentTarget)}
                    onClick={variant.getCheckboxClickHandler?.(fieldName)}
                    invalidMessage={validations[fieldName]?.helperText.istrue}
                    dataCy={`${variant.formType}-form-checkbox-${fieldName}`}
                    required={variant.passCheckboxRequired ? required : undefined}
                    {...(variant.getCheckboxAdditionalProps?.(fieldName) ?? {})}
                >
                    {fieldType === 'checkbox legal' ? (
                        <div className="legal-copy">{fieldLabel}</div>
                    ) : (
                        <Text tag="div" variant="caption" style={{ color: 'var(--base-contrast-subtle)' }}>
                            {fieldLabel}
                        </Text>
                    )}
                </Checkbox>
            );
        }

        if (fieldType === 'radio button') {
            return (
                <RadioGroup
                    key={cms_id}
                    theme="themeGrey"
                    legend={label && <RenderRTE contentfulDoc={label} />}
                    invalidMessage={validations[fieldName]?.helperText.istrue}
                    invalid={validations[fieldName]?.invalid}
                >
                    {dropdown_radio_button_values?.map((element, index) => (
                        <RadioButton
                            key={element.id}
                            label={element.value}
                            value={element.value}
                            name={fieldName}
                            dataCy={`${variant.formType}-form-radio-answer${index}`}
                        />
                    ))}
                </RadioGroup>
            );
        }

        if (fieldType === 'text area') {
            const fieldConfig = validationConfig.fields[fieldName];
            const messageType = getValidationMessageType(fieldConfig, validations[fieldName]);

            return (
                <Textarea
                    key={cms_id}
                    className={width === '50%' ? variant.styles.halfWidth : variant.styles.fullWidth}
                    name={fieldName}
                    label={fieldLabel || ''}
                    showCount
                    invalidMessage={validations[fieldName]?.helperText[messageType]}
                    required={required}
                    onBlur={(event: React.FocusEvent<HTMLTextAreaElement, Element>) =>
                        validateInput(event.currentTarget as unknown as HTMLInputElement)
                    }
                    invalid={validations[fieldName]?.invalid}
                />
            );
        }

        if (fieldType === 'zip code') {
            const fieldConfig = validationConfig.fields[fieldName];
            const messageType = getValidationMessageType(fieldConfig, validations[fieldName]);

            return (
                <Input
                    wrapperId={width === '50%' ? 'halfWidth' : 'fullWidth'}
                    key={cms_id}
                    ref={getInputRef(fieldName)}
                    type="zip"
                    name={fieldName}
                    label={fieldLabel}
                    invalid={validations[fieldName]?.invalid}
                    required={required}
                    onBlur={(event) => validateInput(event.currentTarget)}
                    invalidMessage={validations[fieldName]?.helperText[messageType]}
                    dataCy={`${variant.formType}-form-${fieldName}`}
                    errorDataCy={`${variant.formType}-form-${fieldName}-err`}
                    aria-label={aria_label}
                    inputWrapperStyles={width === '50%' ? variant.styles.halfWidth : variant.styles.fullWidth}
                    inputMode={inputProps.inputMode}
                    maxLength={inputProps.maxLength}
                    transformValue={inputProps.transformValue}
                />
            );
        }

        if (fieldType === 'radio id/vin') {
            const fieldConfig = validationConfig.fields[fieldName];
            const messageType = getValidationMessageType(fieldConfig, validations[fieldName]);

            return (
                <RadioIdVinInput
                    wrapperId={width === '50%' ? 'halfWidth' : 'fullWidth'}
                    key={cms_id}
                    inputRef={getInputRef(fieldName)}
                    name={fieldName}
                    label={fieldLabel}
                    invalid={validations[fieldName]?.invalid}
                    required={required}
                    onBlur={(event) => validateInput(event.currentTarget)}
                    invalidMessage={validations[fieldName]?.helperText[messageType]}
                    dataCy={`${variant.formType}-form-${fieldName}`}
                    errorDataCy={`${variant.formType}-form-${fieldName}-err`}
                    aria-label={aria_label}
                    inputWrapperStyles={width === '50%' ? variant.styles.halfWidth : variant.styles.fullWidth}
                    // maxLength={inputProps.maxLength}
                    transformValue={inputProps.transformValue}
                    onSearchByLicensePlate={onSearchByLicensePlate}
                />
            );
        }

        const fieldConfig = validationConfig.fields[fieldName];
        const messageType = getValidationMessageType(fieldConfig, validations[fieldName]);
        const invalidMessage = validations[fieldName]?.helperText[messageType];

        return (
            <Input
                wrapperId={width === '50%' ? 'halfWidth' : 'fullWidth'}
                key={cms_id}
                {...(fieldType === 'email only'
                    ? {
                          ref: (input: HTMLInputElement | null) => {
                              emailRef.current = input;
                              getInputRef(fieldName).current = input;
                          },
                      }
                    : fieldType === 'email/confirmation'
                      ? {
                            ref: (input: HTMLInputElement | null) => {
                                confirmEmailRef.current = input;
                                getInputRef(fieldName).current = input;
                            },
                        }
                      : { ref: getInputRef(fieldName) })}
                type={getInputType(fieldType)}
                name={fieldName}
                label={fieldLabel}
                className={variant.styles.inputStyles}
                invalid={validations[fieldName]?.invalid}
                required={required}
                onBlur={(event) => validateInput(event.currentTarget)}
                invalidMessage={invalidMessage}
                dataCy={`${variant.formType}-form-${fieldName}`}
                errorDataCy={`${variant.formType}-form-${fieldName}-err`}
                aria-label={aria_label}
                inputWrapperStyles={width === '50%' ? variant.styles.halfWidth : variant.styles.fullWidth}
                inputMode={inputProps.inputMode}
                maxLength={inputProps.maxLength}
                transformValue={inputProps.transformValue}
            />
        );
    });

    const wrappedFieldElements = wrapFormElements(formFieldElements, variant.styles.wrapperRow);

    const duplicateErrorMessage =
        submitState === 'duplicate' && validations.form?.validations.formerror
            ? validations.form.helperText.formerror
            : null;

    const duplicateError =
        submitState === 'duplicate' && validations.form && validations.form.validations.formerror ? (
            <StyledContainer>
                <Text tag="div" role="alert">
                    {validations.form.helperText.formerror}
                </Text>
            </StyledContainer>
        ) : null;
    const formLevelErrorMessage =
        submitState !== 'duplicate' && validations.form?.validations.formerror
            ? validations.form.helperText.formerror
            : undefined;
    const isFlexibleFormAlertMessage =
        isValidElement(formLevelErrorMessage) && formLevelErrorMessage.type === FlexibleFormAlert;

    const submitButton = (
        <Button
            type="submit"
            ariaLabel={ctaFields?.link_text}
            variant={ctaFields?.theme}
            size={ctaFields?.size.toLowerCase()}
            fitting={variant.submitButtonFitting}
            disabled={variant.submitButtonDisabled}
            dataCy={`${variant.formType}-form-submit-button`}
        >
            {ctaFields?.link_text}
        </Button>
    );

    let errorDisplay: ReactNode = null;

    if (isFlexibleFormAlertMessage) {
        errorDisplay = <div className={styles.formAlertSpacing}>{formLevelErrorMessage}</div>;
    } else if (variant.trackSubmitAttempted) {
        errorDisplay = (
            <FormErrorDisplay errorCount={submitAttempted ? errorCount : 0} errorMessage={formLevelErrorMessage} />
        );
    } else if (formLevelErrorMessage) {
        errorDisplay = <FormErrorDisplay errorCount={0} errorMessage={formLevelErrorMessage} />;
    }

    function onSubmit(event: React.SyntheticEvent, data: FormData) {
        if (variant.trackSubmitAttempted) {
            setSubmitAttempted(true);
        }

        const isValid = validateForm(event, true);
        if (!isValid) {
            return false;
        }

        if (variant.onValidSubmit) {
            variant.onValidSubmit(data, context);
        }

        if (variant.customSubmit) {
            void variant.customSubmit(data, context).catch(() => {
                setSubmitState('failed');
            });

            return false;
        }

        return true;
    }

    function onFail() {
        setSubmitState('failed');
    }

    async function onSuccess(response: Response) {
        await variant.onSuccessResponse(response, context);
    }

    function beforeSubmit(data: FormData) {
        return normalizeFormData(data, variant.sourceName);
    }

    const content = variant.renderContent({
        asset,
        nestedForm,
        wrappedFieldElements,
        duplicateError,
        duplicateErrorMessage,
        submitButton,
        errorDisplay,
        validations,
        setValidations,
        submitState,
        setSubmitState,
    });

    const form = (
        <Form
            action={ctaFields?.href}
            method="POST"
            contentType={asset.fields.content_type as FormProps['contentType']}
            onSubmit={onSubmit}
            onFail={onFail}
            onSuccess={onSuccess}
            onBeforeSubmit={beforeSubmit}
            onChange={validateForm}
            novalidate={true}
            dataCy={`${variant.formType}-form`}
        >
            {hiddenFields}
            {content}
        </Form>
    );

    const submitMessage = variant.renderSubmitMessage({ asset, submitState });
    return <>{variant.renderShell({ submitState, submitMessage, form })}</>;
}
